import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import BranchChangeDiffModal from "../components/DhlBranch/BranchChangeDiffModal";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import api from "../services/httpClient";
import "../assets/styles/dhlBranch.scss";

const BranchChangeForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    branchId: "",
    executionDate: "",
    rollbackDate: "",
    postcodes: "",
  });

  const [branches, setBranches] = useState([]);
  const [diffData, setDiffData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pobranie listy oddziałów do dropdown (sortowanie EX/DR alfabetycznie)
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await api.post(`/Branch/List`, {
          sort: "Ex",
          order: "asc",
          offset: 0,
          limit: 1000,
          search: {},
        });
        const result = response.data;
        const rows = Array.isArray(result.items) ? result.items : [];
        // Dodatkowe sortowanie po EX/DR — frontend side, bo backend sortuje tylko po jednej kolumnie
        rows.sort((a, b) => {
          const labelA = `${a.ex || ""}/${a.dr || ""}`.toUpperCase();
          const labelB = `${b.ex || ""}/${b.dr || ""}`.toUpperCase();
          return labelA.localeCompare(labelB);
        });
        setBranches(rows);
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Krok 1: Preview — pobranie diff
  const handlePreview = async (e) => {
    e.preventDefault();

    if (!formData.branchId) {
      alert("Wybierz terminal docelowy.");
      return;
    }
    if (!formData.postcodes.trim()) {
      alert("Wprowadź kody pocztowe.");
      return;
    }

    try {
      const response = await api.post(`/Branch/PreviewChange`, {
        branchId: Number(formData.branchId),
        postcodesRaw: formData.postcodes,
      });

      const result = Array.isArray(response.data) ? response.data : [];

      // Jeśli brak daty wykonania — potwierdzenie natychmiastowego wykonania
      if (!formData.executionDate) {
        const confirmed = window.confirm(
          "Nie podano daty wykonania.\n\n" +
          "Czy zmiana ma zostać wykonana natychmiastowo?\n" +
          "(Czas realizacji zależy od ilości danych do przetworzenia)"
        );
        if (!confirmed) return;
      }

      setDiffData(result);
      setShowModal(true);
    } catch (error) {
      console.error("Error previewing change:", error);
      const msg =
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        "Błąd podczas podglądu zmian.";
      alert(msg);
    }
  };

  // Krok 2: Confirm — zatwierdzenie
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/Branch/ConfirmChange`, {
        branchId: Number(formData.branchId),
        postcodesRaw: formData.postcodes,
        executionDate: formData.executionDate || null,
        rollbackDate: formData.rollbackDate || null,
      });

      setShowModal(false);
      alert(formData.executionDate
        ? "Aktualizacja została dodana do zaplanowanych zmian."
        : "Aktualizacja została zlecona do natychmiastowego wykonania.");
      navigate("/administration/dhl-branches");
    } catch (error) {
      console.error("Error confirming change:", error);
      const msg =
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        "Błąd podczas zatwierdzania zmian.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dhl-branch-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="Branch Change Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Przepnij kody pocztowe
            </h3>
          </Col>
        </Row>

        <Form onSubmit={handlePreview}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="branchId">
                <Form.Label>Terminal docelowy *</Form.Label>
                <Form.Select
                  value={formData.branchId}
                  onChange={(e) => handleChange("branchId", e.target.value)}
                  required
                >
                  <option value="">-- Wybierz terminal --</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.ex}/{b.dr}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="executionDate">
                <Form.Label>Data wykonania (opcjonalna)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.executionDate}
                  onChange={(e) => handleChange("executionDate", e.target.value)}
                />
                <Form.Text className="text-muted">
                  Brak daty = natychmiastowe wykonanie.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="rollbackDate">
                <Form.Label>Data wycofania (opcjonalna)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.rollbackDate}
                  onChange={(e) => handleChange("rollbackDate", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="postcodes">
                <Form.Label>Kody pocztowe *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  placeholder={"Jeden kod na linię (znak enter) chyba, że wymieniane po przecinku, np.:\n00-001, 00-002, 00-003\nlub podając zakres: 00-101*00-205"}
                  value={formData.postcodes}
                  onChange={(e) => handleChange("postcodes", e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Kody można rozdzielać nową linią lub przecinkiem. Zakres: 00-101*00-205.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Button variant="primary" type="submit">
                Zapisz
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      <BranchChangeDiffModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirm}
        diffData={diffData}
        submitting={submitting}
      />
    </div>
  );
};

export default BranchChangeForm;
